import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from './AuthContext';
import { Container, Typography, Button, Box, Card, CardContent, Modal, Fade, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import LoginPage from "./pages/login/Login";
import LogoBlog from "./assets/logo.png";

const didYouKnowFacts = [
  "Effective time management can increase productivity by up to 20%.",
  "The Pomodoro Technique uses 25-minute work sessions to boost focus.",
  "Regular breaks can improve mental agility and reduce fatigue.",
  "Task prioritization helps reduce stress and meet deadlines.",
  "The Eisenhower Matrix categorizes tasks by urgency and importance.",
  "Multitasking can reduce productivity by up to 40%.",
  "Setting SMART goals improves project success rates.",
  "Collaboration tools can cut email usage by 30%.",
  "A tidy workspace can enhance focus and efficiency.",
  "Daily planning can save hours of wasted time weekly.",
  "The 80/20 rule suggests 20% of tasks produce 80% of results.",
  "Time tracking helps identify productivity bottlenecks.",
  "Delegation improves team efficiency and morale.",
  "Mind mapping aids in brainstorming and idea organization.",
  "The Kanban method visualizes workflow for better task management.",
  "Regular feedback loops improve team performance.",
  "Setting deadlines increases task completion rates.",
  "Morning routines can set a positive tone for the workday.",
  "The Zeigarnik Effect makes unfinished tasks more memorable.",
  "Batch processing similar tasks saves time and mental energy.",
  "Effective meetings can reduce project delays by 15%.",
  "The Pareto Principle helps focus on high-impact tasks.",
  "Automating repetitive tasks can save hours daily.",
  "Clear communication reduces workplace errors.",
  "Goal setting aligns teams with organizational objectives.",
  "The 2-Minute Rule encourages starting small tasks immediately.",
  "Time-blocking enhances focus on high-priority tasks.",
  "Regular training improves employee skillsets.",
  "A positive work environment boosts productivity.",
  "Task delegation empowers team members.",
  "The Getting Things Done (GTD) method organizes tasks effectively.",
  "Mindfulness practices reduce workplace stress.",
  "Prioritizing sleep improves cognitive performance.",
  "The 4D method: Do, Defer, Delegate, or Delete tasks.",
  "Project management software streamlines workflows.",
  "The Feynman Technique enhances learning through teaching.",
  "Regular reviews help track project progress.",
  "The Rule of Three focuses on three key daily tasks.",
  "Active listening improves team collaboration.",
  "Setting boundaries prevents work overload.",
  "The Ivy Lee Method prioritizes six tasks daily.",
  "Time audits reveal how time is actually spent.",
  "The Agile methodology enhances team adaptability.",
  "Regular exercise boosts workplace energy levels.",
  "The 1-3-5 Rule: Plan 1 big, 3 medium, and 5 small tasks daily.",
  "Effective onboarding reduces employee turnover.",
  "The Scrum framework improves team productivity.",
  "Taking notes enhances task retention.",
  "The OKR framework aligns goals with measurable results.",
  "Continuous learning fosters innovation.",
];

const HomePage = () => {
  const { isAuthenticated, userId, userRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openLoginModal, setOpenLoginModal] = useState(false);
  const [currentFactIndex, setCurrentFactIndex] = useState(0);

  // Redirect unauthenticated users to login
  useEffect(() => {
    if (isAuthenticated) {
      const intendedPath = location.state?.from?.pathname || 
                          (userRole === 'user' ? '/user/dashboard' : '/admin/dashboard');
      navigate(intendedPath, { replace: true });
    }
  }, [isAuthenticated, userRole, location, navigate]);

  // Cycle through "Did You Know" facts every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFactIndex((prevIndex) => (prevIndex + 1) % didYouKnowFacts.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleOpenLogin = () => {
    setOpenLoginModal(true);
  };

  const handleCloseLogin = () => {
    setOpenLoginModal(false);
  };

  return (
    <Box>
      <Box width={'100%'} bgcolor={'#fff'} p={1} display={'flex'} justifyContent={'center'} alignItems={'center'}>
        <Box width={'90%'} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
          <Box width={'150px'}>
            <img style={{ width: '100%' }} src={LogoBlog} alt='Homr-logo' />
          </Box>
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={handleOpenLogin}
            sx={{ px: 1.5, py: 1.5 }}
          >
            Open My Office
          </Button>
        </Box>
      </Box>    
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '85vh' }}>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        
        <Typography variant="h1" component="h1" gutterBottom sx={{ textAlign: 'center', color: '#1976d2' }}>
          Welcome to Mindsinaction, {userId || 'Employee'}!
        </Typography>
        <Typography variant="h5" sx={{ textAlign: 'center', mb: 4, color: 'text.secondary' }}>
          Your work management system to streamline tasks, collaborate, and achieve goals.
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4, mt: 8 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleOpenLogin}
            sx={{ px: 4, py: 1.5 }}
          >
            Open My Office
          </Button>
        </Box>

        <Card sx={{ maxWidth: 600, mx: 'auto', boxShadow: 3, background: '#45917eff' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" component="h2">
                Did You Know?
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ minHeight: 60, color: 'text.primary' }}>
              {didYouKnowFacts[currentFactIndex]}
            </Typography>
          </CardContent>
        </Card>

        <Modal
          open={openLoginModal}
          onClose={handleCloseLogin}
          closeAfterTransition
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Fade in={openLoginModal}>
            <Box sx={{ 
              outline: 'none', 
              position: 'relative', 
              borderRadius: 2, 
              width: 'auto',
              height: 'auto',
              overflow: 'hidden'
            }}>
              <LoginPage />
            </Box>
          </Fade>
        </Modal>
      </Container>
    </Box>
  );
};

export default HomePage;